import { Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { AuthenticationService } from '../services/authentication.service';
import { UserModel } from '../models/user.model';

@Directive({
    selector: '[can]'
})
export class CanDirective {

    protected isVisible = false;

    constructor(protected templateRef: TemplateRef<any>, protected viewContainer: ViewContainerRef, protected authenticationService: AuthenticationService) {
    }

    /**
     * Conditionally includes a template based on the value of an expression.
     *
     * Simple:
     * - `<div *can="'permission'">...</div>`
     *
     * With or statements:
     * ```
     * <div *can="'permission; or another-permission'">...</div>
     * ```
     *
     * With and statements
     * ```
     * <div *can="'permission; and another-permission'"></div>
     * ```
     *
     * With or and and statements
     * ```
     * <div *can="'permission; and another-permission; or yet-another-permission"></div>
     * ```
     */
    @Input()
    set can(permissions: string) {
        permissions = permissions.trim();

        const user = this.authenticationService.user();

        user.subscribe((user: UserModel) => {
            this.hasPermissionsFromString(user, permissions).then(() => {

                // If condition is true  and the current state is
                // not visisble add the template to the DOM
                if (!this.isVisible) {
                    this.viewContainer.createEmbeddedView(this.templateRef);
                    this.isVisible = true;
                }

            }, (error) => {

                // When no permission is found on the current user and
                // the current state is visible ->
                // remove template from DOM
                if (this.isVisible) {
                    this.viewContainer.clear();
                    this.isVisible = false;
                }

            });
        });
    }

    /**
     * Extract permissions from the given expression and check if the authenticated user has those
     * permissions.
     *
     * @param {UserModel} user
     * @param permissions
     * @returns {Promise<any>}
     */
    protected hasPermissionsFromString(user: UserModel, input: string): Promise<any> {
        return new Promise((resolve, reject) => {
            // Extract all or statements
            const statements = input.split('; or');

            // Loop through all or statements
            statements.forEach((statement: string, index: number) => {
                // Extract all and statements
                const permissions = statement.split('; and');

                // Remove whitespaces
                this.trim(permissions);

                // Check if the user has the extracted permissions
                this.hasPermissions(user, permissions).then(() => {
                    resolve();
                }, (error) => {
                    reject(error);
                });
            }, (error) => {
                reject(error);
            });
        });
    }

    /**
     * Check if the authenticated user has the given permissions.
     *
     * @param {UserModel} user
     * @param {string | Array<string>} permissions
     * @returns {Promise<any>}
     */
    protected hasPermissions(user: UserModel, permissions: Array<string>): Promise<any> {
        return new Promise((resolve, reject) => {
            let resolved = 0;

            // Loop through all permissions
            for (const permission of permissions) {

                // Check if the user has the permission
                this.hasPermission(user, permission).then(() => {
                    resolved++;

                    // Resolve if the user has all the given permissions
                    if (resolved === permissions.length) {
                        resolve(permissions);
                    }
                }, (error) => {
                    reject(error);
                });
            }
        });
    }

    /**
     * Check if the authenticated user has the given permission.
     *
     * @param {UserModel} user
     * @param {string} permission
     * @returns {Promise<any>}
     */
    protected hasPermission(user: UserModel, permission: string): Promise<any> {
        return new Promise((resolve, reject) => {
            if (user.can(permission)) {
                resolve(permission);
            } else {
                reject(permission);
            }
        });
    }

    /**
     * Trim all items of a string array.
     *
     * @param {Array<string>} array
     */
    protected trim(array: Array<string>): void {
        array.forEach((and: string, index: number) => {
            array[index] = and.trim();
        });
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/authentication/directives/can.directive.ts